import React, { lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ViewType } from './Layout';

// Lazy-load every view so each route's code is only fetched when first visited.
// Vite splits each dynamic import into its own chunk automatically.
const DashboardContent    = lazy(() => import('../dashboard/DashboardContent').then(m => ({ default: m.DashboardContent })));
const WorkflowListPage    = lazy(() => import('../workflows/list/WorkflowListPage').then(m => ({ default: m.WorkflowListPage })));
const ExecutionLogsTable  = lazy(() => import('../logs/ExecutionLogsTable').then(m => ({ default: m.ExecutionLogsTable })));
const PluginManager       = lazy(() => import('../plugins').then(m => ({ default: m.PluginManager })));
const PluginSettings      = lazy(() => import('../plugins').then(m => ({ default: m.PluginSettings })));
const SettingsPage        = lazy(() => import('../settings').then(m => ({ default: m.SettingsPage })));
const SecretsPage         = lazy(() => import('../secrets').then(m => ({ default: m.SecretsPage })));
const CredentialsPage     = lazy(() => import('../credentials').then(m => ({ default: m.CredentialsPage })));
const BillingPage         = lazy(() => import('../billing').then(m => ({ default: m.BillingPage })));
const FormsPage           = lazy(() => import('../forms').then(m => ({ default: m.FormsPage })));

// Admin views — grouped into a single chunk via vite manualChunks
const AdminDashboard            = lazy(() => import('../admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminTenantsPage          = lazy(() => import('../admin/AdminTenantsPage').then(m => ({ default: m.AdminTenantsPage })));
const AdminTenantDetailPage     = lazy(() => import('../admin/AdminTenantDetailPage').then(m => ({ default: m.AdminTenantDetailPage })));
const AdminUsersPage            = lazy(() => import('../admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminCreditsPage          = lazy(() => import('../admin/AdminCreditsPage').then(m => ({ default: m.AdminCreditsPage })));
const AdminSettingsPage         = lazy(() => import('../admin/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const AdminBillingParametersPage = lazy(() => import('../admin/AdminBillingParametersPage').then(m => ({ default: m.AdminBillingParametersPage })));
const AdminPlansPage            = lazy(() => import('../admin/AdminPlansPage').then(m => ({ default: m.AdminPlansPage })));
const AdminPluginsPage          = lazy(() => import('../admin/AdminPluginsPage').then(m => ({ default: m.AdminPluginsPage })));
const AdminTemplatesPage        = lazy(() => import('../admin/AdminTemplatesPage').then(m => ({ default: m.AdminTemplatesPage })));
const AdminPromoCodesPage       = lazy(() => import('../admin/AdminPromoCodesPage').then(m => ({ default: m.AdminPromoCodesPage })));
const AdminReferralPage         = lazy(() => import('../admin/AdminReferralPage').then(m => ({ default: m.AdminReferralPage })));
const AdminSubscriptionsPage    = lazy(() => import('../admin/AdminSubscriptionsPage').then(m => ({ default: m.AdminSubscriptionsPage })));
const AdminInvoicesPage         = lazy(() => import('../admin/AdminInvoicesPage').then(m => ({ default: m.AdminInvoicesPage })));
const AdminEmailAuditLogsPage   = lazy(() => import('../admin/AdminEmailAuditLogsPage').then(m => ({ default: m.AdminEmailAuditLogsPage })));

const ViewLoader = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

export interface WorkAreaProps {
  currentView: ViewType;
  children?: React.ReactNode;
}

export const WorkArea: React.FC<WorkAreaProps> = ({ currentView, children }) => {
  const location = useLocation();
  
  const renderContent = () => {
    // If children are provided, render them (for custom content)
    if (children) {
      return children;
    }

    // Handle plugin-specific routes
    if (currentView === 'plugins') {
      if (location.pathname.includes('/settings')) {
        return <PluginSettings />;
      }
      return <PluginManager />;
    }

    // Otherwise render based on current view
    switch (currentView) {
      // Tenant views
      case 'dashboard':
        return <DashboardContent />;
      case 'workflows':
        return <WorkflowListPage />;
      case 'logs':
        return <ExecutionLogsTable />;
      case 'secrets':
        return <SecretsPage />;
      case 'credentials':
        return <CredentialsPage />;
      case 'billing':
        return <BillingPage />;
      case 'settings':
        return <SettingsPage />;
      case 'forms':
        return <FormsPage />;
      
      // Platform Admin views
      case 'admin-dashboard':
        return <AdminDashboard />;
      case 'admin-tenants':
        // Check if we're on a tenant detail page
        if (location.pathname.match(/\/admin\/tenants\/[^/]+$/)) {
          return <AdminTenantDetailPage />;
        }
        return <AdminTenantsPage />;
      case 'admin-users':
        return <AdminUsersPage />;
      case 'admin-credits':
        return <AdminCreditsPage />;
      case 'admin-billing-parameters':
        return <AdminBillingParametersPage />;
      case 'admin-plans':
        return <AdminPlansPage />;
      case 'admin-subscriptions':
        return <AdminSubscriptionsPage />;
      case 'admin-invoices':
        return <AdminInvoicesPage />;
      case 'admin-plugins':
        return <AdminPluginsPage />;
      case 'admin-templates':
        return <AdminTemplatesPage />;
      case 'admin-promo-codes':
        return <AdminPromoCodesPage />;
      case 'admin-referral-codes':
        return <AdminReferralPage />;
      case 'admin-email-audit-logs':
        return <AdminEmailAuditLogsPage />;
      case 'admin-settings':
        return <AdminSettingsPage />;
        
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-600 mb-4">
                Welcome to Enveu System
              </h2>
              <p className="text-gray-500">
                Select an option from the sidebar to get started
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-background">
      <div className="h-full">
        <Suspense fallback={<ViewLoader />}>
          {renderContent()}
        </Suspense>
      </div>
    </div>
  );
};